import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import EventsStore from '../../stores/EventStore/Events.store'
import InitialState from '../../stores/InitialState'
import SavePasswordStore from './SavePassword.store'

interface IProps {
  eventsStore?: EventsStore
  state?: InitialState
}

/**
 * STX1-1057: asks for a password when it is the user's only way back in.
 * Opened by the INIT_APP_3 gate (shouldPromptForPassword).
 */
@inject('eventsStore', 'state')
@observer
export default class SavePasswordDialog extends React.Component<IProps> {
  private readonly store: SavePasswordStore

  public constructor(props: IProps) {
    super(props)

    this.store = new SavePasswordStore(props.eventsStore)
  }

  public render() {
    if (!this.props.state.isSavePasswordPromptOpen) {
      return null
    }

    const { password, confirmationPassword, passwordChangeError, isSaving } =
      this.store

    const helperText = password
      ? passwordChangeError
      : Localization.translator.passwordErrors.weakPassword
    const helperClassName = password
      ? 'tw:text-body-sm tw:text-destructive'
      : 'tw:text-body-sm tw:text-muted-foreground'

    return (
      <Dialog
        open={true}
        onOpenChange={this.handleOpenChange}
        disablePointerDismissal={true}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{Localization.translator.setYourPassword}</DialogTitle>
            <DialogDescription>
              {Localization.translator.setPasswordToSignBackIn}
            </DialogDescription>
          </DialogHeader>

          <StruxhubInput
            type="password"
            label={Localization.translator.password}
            isRequiredTextHidden={true}
            isRequired={true}
            value={password || ''}
            isDisabled={isSaving}
            onChange={this.onPasswordChange}
          />
          <StruxhubInput
            type="password"
            label={Localization.translator.confirmPassword}
            isRequiredTextHidden={true}
            isRequired={true}
            value={confirmationPassword || ''}
            isDisabled={isSaving}
            onChange={this.onConfirmationPasswordChange}
            onKeyDown={this.handleKeyDown}
          />

          <div className={helperClassName}>{helperText}</div>

          <DialogFooter className="tw:mx-0 tw:mb-0 tw:rounded-b-none tw:border-t-0 tw:bg-transparent tw:p-0">
            <Button
              variant="outline"
              onClick={this.handleDismiss}
              disabled={isSaving}
            >
              {Localization.translator.dismiss}
            </Button>
            <Button
              onClick={this.store.handleSetPassword}
              disabled={!!passwordChangeError || isSaving}
            >
              {isSaving && <Spinner size="sm" variant="white" />}
              {Localization.translator.setPassword}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  private onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.store.password = e.currentTarget.value
    this.store.resetError()
  }

  private onConfirmationPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.store.confirmationPassword = e.currentTarget.value
    this.store.resetError()
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      this.store.handleSetPassword()
    }
  }

  private handleOpenChange = (open: boolean) => {
    // Escape reaches here even while the button is disabled. Closing mid-save
    // would strand a failure: onSaveFailed writes the server error into a
    // dialog that is no longer rendered, and the dismissal keeps the gate from
    // reopening it, so the user is left with no password and no message.
    if (!open && !this.store.isSaving) {
      this.handleDismiss()
    }
  }

  private handleDismiss = () => {
    // Walking away from the prompt means walking away from what was typed;
    // this store outlives the dialog, so leave nothing behind in it.
    this.store.clear()
    this.props.state.isSavePasswordPromptDismissed = true
    this.props.state.isSavePasswordPromptOpen = false
  }
}
